// packages/portal/src/contexts/ModulesProvider.jsx
//
// Fetches the signed-in user's paid-module entitlements and feeds them to the shared
// resolver. Spec: 20260818-invoicingsystem/spec-00-modules.md §4.1. Tasks T027.
//
// ── FAIL CLOSED WHILE LOADING ───────────────────────────────────────────────
//
// `rows === null` makes the shared ModuleProvider resolve EVERY module closed. These are
// paid surfaces: rendering one optimistically for the duration of a fetch would flash a
// module the customer has not bought. Unlike the resident-app feature stack there is no
// permissive default here — closed is the only safe starting point.
//
// A failed fetch also stays closed. The alternative — "assume enabled on error" — turns
// an API blip into free access, and the server would refuse the writes anyway, so the
// user would get a broken surface instead of an absent one.
//
// ── THIS IS AN AFFORDANCE LAYER ─────────────────────────────────────────────
//
// Everything decided here only hides buttons. requireModule / requireModuleHistory on
// the API is the enforcement point. If the two ever disagree, the server is right.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ModuleProvider } from '@caro/shared/modules';
import { modulesApi } from '../services/apiClient';
import { useAuth } from './AuthContext';

const ModulesFetchContext = createContext({ loading: true, error: null, refresh: () => {} });

/** Fetch state for the entitlements, separate from the resolved map itself. */
export const useModulesFetch = () => useContext(ModulesFetchContext);

export function ModulesProvider({ children }) {
  const auth = useAuth();
  const isAuthenticated = !!(auth && auth.isAuthenticated);

  // null (not []) until the first successful response — that is what seeds the shared
  // resolver closed. An empty array would mean "fetched, nothing bought", which is a
  // different and equally valid state.
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setRows(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await modulesApi.getMine();
      const modules = (res && res.data && res.data.data && res.data.data.modules) || [];
      // The endpoint returns already-resolved entries; map them back to the row shape the
      // shared resolver consumes so there is exactly ONE resolution rule in the codebase
      // rather than a second one hidden in this component.
      setRows(
        modules.map((m) => ({
          module: m.key,
          enabled: !!m.enabled,
          valid_until: m.validUntil ?? null,
          enabled_at: m.enabledAt ?? (m.everEnabled ? new Date(0).toISOString() : null),
        }))
      );
      setError(null);
    } catch (err) {
      // Stay closed. See the header.
      setRows(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ModulesFetchContext.Provider value={{ loading, error, refresh: load }}>
      <ModuleProvider rows={rows}>{children}</ModuleProvider>
    </ModulesFetchContext.Provider>
  );
}

export default ModulesProvider;
